//DashboardRecharts.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { globalDatafeedInstance } from '@/components/component/dashboard/trading-dashboard-utils';
import {
  calculateHMA,
  calculateEMA,
  calculateKeltnerChannels,
  calculateMedianPrice,
  calculateTrendMA,
  calculateVWAP,
  calculateRegression,
} from '@/components/component/chart-page/charts/WatchPricesChart/utils/calculations';

// Cache for storing processed data by symbol
const dataCache = new Map<string, any[]>();

interface WatchPrice {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface DashboardRechartsProps {
  selectedSymbol: string;
  onTimeframeChange?: (timeframe: string) => void;
}

const DashboardRecharts: React.FC<DashboardRechartsProps> = ({
  selectedSymbol,
  onTimeframeChange,
}) => {
  const [timeframe, setTimeframe] = useState<string>('1M');
  const [chartData, setChartData] = useState<WatchPrice[]>([]);
  const [processedData, setProcessedData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showVolume, setShowVolume] = useState<boolean>(false);
  const [chartType, setChartType] = useState<'linear' | 'logarithmic'>(
    'linear'
  );
  const [indicators, setIndicators] = useState<string[]>(['price', 'ema20']);
  const hasInitializedRef = useRef<Set<string>>(new Set());

  // Extract model name for display
  const modelName = selectedSymbol?.split(':')?.[1] || selectedSymbol;
  const brandName = selectedSymbol?.split(':')?.[0] || 'Rolex';

  // Function to fetch watch data - use cached data if available
  const fetchWatchData = async (symbol: string) => {
    // Check cache first
    if (dataCache.has(symbol)) {
      console.log(`Using cached data for ${symbol}`);
      return dataCache.get(symbol);
    }

    try {
      console.log(`Fetching data for ${symbol}`);
      setIsLoading(true);

      // Try using the datafeed first
      if (globalDatafeedInstance) {
        const bars =
          await globalDatafeedInstance.initializeDataForSymbol(symbol);
        if (bars && Array.isArray(bars) && bars.length > 0) {
          console.log(
            `Using data from datafeed: ${bars.length} bars available`
          );
          // Store in cache
          dataCache.set(symbol, bars);
          return bars;
        }
      }

      // Fallback to direct API call
      const response = await fetch(
        `/api/trading-dashboard/getBars?symbol=${encodeURIComponent(symbol)}&resolution=1D&source=forum`
      );

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          console.log(`Fetched ${data.length} bars from API`);
          // Ensure time is in milliseconds
          const processedBars = data.map((bar) => ({
            ...bar,
            time:
              typeof bar.time === 'number' && bar.time < 1e12
                ? bar.time * 1000
                : bar.time,
          }));

          // Store in cache
          dataCache.set(symbol, processedBars);
          return processedBars;
        }
      }

      throw new Error('Failed to fetch data');
    } catch (error) {
      console.error('Error fetching watch data:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when selected symbol changes
  useEffect(() => {
    const loadData = async () => {
      if (!selectedSymbol) return;

      // Skip if already initialized
      if (hasInitializedRef.current.has(selectedSymbol)) {
        // Just reuse the existing data
        const cachedData = dataCache.get(selectedSymbol);
        if (cachedData) {
          setChartData(cachedData);
          return;
        }
      }

      setIsLoading(true);
      try {
        const data = await fetchWatchData(selectedSymbol);
        // Check if data exists before setting it
        if (data && Array.isArray(data)) {
          setChartData(data);
        } else {
          setChartData([]); // Set to empty array if no data
        }

        // Mark as initialized
        hasInitializedRef.current.add(selectedSymbol);
      } catch (error) {
        console.error('Error loading watch data:', error);
        setChartData([]); // Set to empty array on error
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [selectedSymbol]);

  // Process data with calculations
  useEffect(() => {
    if (!chartData.length) return;

    try {
      // Convert chart data to the format needed for calculations
      const formattedData = chartData.map((item) => ({
        date: new Date(item.time).toISOString(),
        price: item.close,
        formattedDate: format(new Date(item.time), 'MMM d, yyyy'),
        ma50: null,
        volume: item.volume || 1,
      }));

      // Apply calculations - same as in WatchPricesChart
      const ema20 = calculateEMA(formattedData, 20);
      const hma20 = calculateHMA(formattedData, 20);
      const median50 = calculateMedianPrice(formattedData, 50);
      const trendMA = calculateTrendMA(formattedData, 30);
      const vwap20 = calculateVWAP(formattedData, 20);
      const keltner = calculateKeltnerChannels(formattedData);
      const regression = calculateRegression(formattedData);

      // Combine all data into enriched dataset
      const enrichedData = formattedData.map((point, idx) => ({
        ...point,
        date: new Date(point.date), // Convert to Date object for Recharts
        raw_price: point.price,
        price: ema20[idx] || point.price, // Smoothed price (EMA)
        ema20: ema20[idx],
        hma20: hma20[idx],
        median50: median50[idx],
        trendMA: trendMA[idx],
        vwap: vwap20[idx],
        keltner_middle: keltner[idx]?.middle,
        keltner_upper: keltner[idx]?.upper,
        keltner_lower: keltner[idx]?.lower,
        regression_value: regression[idx]?.value,
        regression_upper: regression[idx]?.upper,
        regression_lower: regression[idx]?.lower,
        open: chartData[idx].open,
        high: chartData[idx].high,
        low: chartData[idx].low,
        close: chartData[idx].close,
      }));

      setProcessedData(enrichedData);
    } catch (error) {
      console.error('Error processing chart data:', error);
      // Fallback to basic formatting if calculations fail
      setProcessedData(
        chartData.map((item) => ({
          date: new Date(item.time),
          formattedDate: format(new Date(item.time), 'MMM d, yyyy'),
          price: item.close,
          open: item.open,
          high: item.high,
          low: item.low,
          volume: item.volume || 0,
        }))
      );
    }
  }, [chartData]);

  // Handle timeframe change
  const handleTimeframeChange = (newTimeframe: string) => {
    setTimeframe(newTimeframe);
    if (onTimeframeChange) {
      onTimeframeChange(newTimeframe);
    }
  };

  // Toggle indicators
  const toggleIndicator = (indicator: string) => {
    setIndicators((prev) =>
      prev.includes(indicator)
        ? prev.filter((i) => i !== indicator)
        : [...prev, indicator]
    );
  };

  // Format date based on timeframe
  const formatXAxis = (tickItem: Date) => {
    if (timeframe === '1D') return format(new Date(tickItem), 'h:mm a');
    if (timeframe === '1W') return format(new Date(tickItem), 'EEE');
    if (timeframe === '1M') return format(new Date(tickItem), 'd MMM');
    if (timeframe === '3M' || timeframe === '6M')
      return format(new Date(tickItem), 'MMM d');
    return format(new Date(tickItem), 'MMM yyyy');
  };

  // Format price
  const formatPrice = (value: number) => {
    return `$${value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  // Generate a consistent color for this symbol
  const symbolColor = useMemo(() => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    const hash = selectedSymbol
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }, [selectedSymbol]);

  // Get indicator color
  const getIndicatorColor = (indicator: string): string => {
    const colorMap: Record<string, string> = {
      price: symbolColor,
      ema20: '#f59e0b',
      hma20: '#14b8a6',
      trendMA: '#8b5cf6',
      vwap: '#ec4899',
      median50: '#84cc16',
      keltner_middle: '#6366f1',
      regression_value: '#f43f5e',
    };
    return colorMap[indicator] || '#888888';
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 bg-slate-800 border border-slate-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-slate-200">
            {format(new Date(label), 'PP')}
          </p>
          <p className="text-blue-400 font-medium text-lg">
            {formatPrice(payload[0].payload.price)}
          </p>
          {payload[0].payload.high && (
            <div className="text-xs text-slate-400 mt-1">
              <div>High: {formatPrice(payload[0].payload.high)}</div>
              <div>Low: {formatPrice(payload[0].payload.low)}</div>
              <div>Open: {formatPrice(payload[0].payload.open)}</div>
            </div>
          )}
          {indicators.includes('ema20') && (
            <div className="text-xs text-orange-400 mt-1">
              EMA20: {formatPrice(payload[0].payload.ema20 || 0)}
            </div>
          )}
          {indicators.includes('hma20') && (
            <div className="text-xs text-teal-400 mt-1">
              HMA20: {formatPrice(payload[0].payload.hma20 || 0)}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (!processedData.length) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-900/90 rounded-lg">
        <p className="text-slate-400">No data available for {selectedSymbol}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {modelName} Price Chart
          </h3>
          <p className="text-sm text-slate-400">{brandName}</p>
        </div>

        <div className="flex space-x-2">
          {['1D', '1W', '1M', '3M', '6M', 'YTD', '1Y', 'ALL'].map((option) => (
            <Badge
              key={option}
              variant="outline"
              className={`cursor-pointer ${
                timeframe === option
                  ? 'bg-blue-600 text-white border-transparent'
                  : 'bg-slate-800/50 text-slate-300 border-slate-700'
              }`}
              onClick={() => handleTimeframeChange(option)}
            >
              {option}
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="outline"
          size="sm"
          className={`${
            chartType === 'linear'
              ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
              : 'bg-slate-800/50 text-slate-400 border-slate-700/50'
          }`}
          onClick={() => setChartType('linear')}
        >
          Linear
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={`${
            chartType === 'logarithmic'
              ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
              : 'bg-slate-800/50 text-slate-400 border-slate-700/50'
          }`}
          onClick={() => setChartType('logarithmic')}
        >
          Log
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={`${
            showVolume
              ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
              : 'bg-slate-800/50 text-slate-400 border-slate-700/50'
          }`}
          onClick={() => setShowVolume(!showVolume)}
        >
          Volume
        </Button>

        {/* Indicator buttons */}
        <Button
          variant="outline"
          size="sm"
          className={`${
            indicators.includes('ema20')
              ? 'bg-orange-600/20 text-orange-400 border-orange-500/30'
              : 'bg-slate-800/50 text-slate-400 border-slate-700/50'
          }`}
          onClick={() => toggleIndicator('ema20')}
        >
          EMA20
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={`${
            indicators.includes('hma20')
              ? 'bg-teal-600/20 text-teal-400 border-teal-500/30'
              : 'bg-slate-800/50 text-slate-400 border-slate-700/50'
          }`}
          onClick={() => toggleIndicator('hma20')}
        >
          HMA20
        </Button>
      </div>

      <div className="flex-grow relative">
        {/* Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 opacity-5">
          <img src="/watchinspect.svg" alt="Logo" className="w-40 h-40" />
        </div>

        {/* Chart */}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#1E293B"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              scale="time"
              type="number"
              domain={['auto', 'auto']}
              tickFormatter={formatXAxis}
              tick={{ fill: '#64748B' }}
              stroke="#334155"
            />
            <YAxis
              domain={['auto', 'auto']}
              tickFormatter={formatPrice}
              tick={{ fill: '#64748B' }}
              stroke="#334155"
              scale={chartType === 'logarithmic' ? 'log' : 'auto'}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Price line */}
            <Line
              type="monotone"
              dataKey="price"
              stroke={symbolColor}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6, fill: symbolColor, stroke: '#FFF' }}
              name={modelName}
            />

            {/* Indicators */}
            {indicators.includes('ema20') && (
              <Line
                type="monotone"
                dataKey="ema20"
                stroke={getIndicatorColor('ema20')}
                strokeWidth={1.5}
                dot={false}
                connectNulls
                name="EMA20"
                strokeDasharray="3 3"
              />
            )}

            {indicators.includes('hma20') && (
              <Line
                type="monotone"
                dataKey="hma20"
                stroke={getIndicatorColor('hma20')}
                strokeWidth={1.5}
                dot={false}
                connectNulls
                name="HMA20"
                strokeDasharray="3 3"
              />
            )}

            {indicators.includes('trendMA') && (
              <Line
                type="monotone"
                dataKey="trendMA"
                stroke={getIndicatorColor('trendMA')}
                strokeWidth={1.5}
                dot={false}
                connectNulls
                name="Trend MA"
                strokeDasharray="3 3"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-xs text-slate-400">Current</div>
          <div className="text-lg font-semibold text-white">
            {formatPrice(processedData[processedData.length - 1]?.price || 0)}
          </div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-xs text-slate-400">Range</div>
          <div className="text-sm font-semibold text-white">
            {formatPrice(
              Math.min(...processedData.map((d) => d.low || d.price))
            )}{' '}
            -{' '}
            {formatPrice(
              Math.max(...processedData.map((d) => d.high || d.price))
            )}
          </div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <div className="text-xs text-slate-400">Avg. Price</div>
          <div className="text-lg font-semibold text-white">
            {formatPrice(
              processedData.reduce((sum, item) => sum + item.price, 0) /
                processedData.length
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardRecharts;
